import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * A short, muted, looping clip that plays only while it is on screen.
 *
 * - `preload="none"`, and the poster is a lazy `<img>` behind the video, not
 *   the video's `poster` attribute (which browsers fetch at once, even far
 *   below the fold): a visitor who never scrolls here pays for nothing.
 * - It plays when at least half of it is visible and pauses when it leaves —
 *   never three clips burning the CPU off-screen. A clip with no size (hidden
 *   visually, e.g. while the PreMize 3D plays the same clip on its screens)
 *   never plays.
 * - Reduced motion or Save-Data: it never starts by itself; the native
 *   controls appear so the visitor can play it on request. They also appear if
 *   the browser refuses autoplay — but not when a pending play() was merely
 *   interrupted by scrolling away (AbortError).
 * - `preload` is written before the <source> child, so the browser knows not
 *   to fetch before it starts resource selection.
 */
export function LoopVideo({
  src,
  poster,
  width,
  height,
  label,
  className,
}: {
  src: string;
  poster: string;
  width: number;
  height: number;
  label: string;
  /** Size, shape and ground of the clip's box (aspect ratio, rounding). */
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const v = ref.current;
    const img = imgRef.current;
    if (!v) return;
    v.muted = true; // React sets `muted` as a property; make sure before play()
    const cleanups: Array<() => void> = [];
    // Native controls paint an opaque panel over the <img> behind the video,
    // so a clip with controls gets its poster too — the img's own URL, once
    // the lazy img has loaded it (no extra request, still lazy).
    const showControls = () => {
      v.controls = true;
      if (!img) return;
      const setPoster = () => {
        if (img.naturalWidth) v.poster = img.currentSrc || img.src;
      };
      if (img.complete) setPoster();
      else {
        img.addEventListener('load', setPoster, { once: true });
        cleanups.push(() => img.removeEventListener('load', setPoster));
      }
    };
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
    if (reduce || saveData) {
      showControls();
      return () => cleanups.forEach((f) => f());
    }
    const play = () =>
      v.play().catch((err: DOMException) => {
        if (err?.name !== 'AbortError') showControls();
      });
    const io = new IntersectionObserver(
      ([e]) => {
        const shown = e.isIntersecting && e.intersectionRatio >= 0.5 && e.boundingClientRect.width > 1;
        if (shown) void play();
        else v.pause();
      },
      { threshold: [0, 0.5] },
    );
    io.observe(v);
    // Hidden visually while it was playing (no new intersection fires for a
    // box that shrinks to nothing): stop it.
    const ro = new ResizeObserver(([e]) => {
      if (e.contentRect.width <= 1) v.pause();
    });
    ro.observe(v);
    return () => {
      io.disconnect();
      ro.disconnect();
      cleanups.forEach((f) => f());
    };
  }, []);

  return (
    <div className={cn('relative overflow-hidden', className)}>
      <img
        ref={imgRef}
        loading="lazy"
        decoding="async"
        src={poster}
        alt=""
        width={width}
        height={height}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <video
        ref={ref}
        preload="none"
        muted
        loop
        playsInline
        width={width}
        height={height}
        aria-label={label}
        className="relative block h-full w-full object-cover"
      >
        <source src={src} type="video/mp4" />
      </video>
    </div>
  );
}
